import React, {useEffect, useState, useContext, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import {collection, doc, getDoc} from 'firebase/firestore';
import moment from 'moment';
import get from 'lodash/get';
import castArray from 'lodash/castArray';
import startCase from 'lodash/startCase';

import {db} from '-/firebase';
import {hasFeature} from '-/features';

import {populateCollectionIds, populateKeyFromCollection, processRawDoc} from '-/data/utils';
import {getRequirementDefinition} from '-/data/requirements';
import {allSections as allSkillDrillSections} from '-/data/skillDrills';

import useDocumentTitle from '-/hooks/useDocumentTitle';
import {reportMeta} from '-/pages/reports/registry';

import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {StationsContext} from '-/contexts/Stations';
import {FileCategoriesContext} from '-/contexts/FileCategories';
import {useGearLocations} from '-/contexts/GearLocations';
import {locationPath, isLocationField, parseLocationValue, LOCATION_KINDS} from '-/pages/gear/redesign/locationValue';
import {GEAR_ROUTE_ITERATIONS} from '-/gearDepth';
import {dutyShiftTypeLabel} from '@embertracking/common';
import {NFPA} from '-/data/standards';
import {stationDisplayName} from '@embertracking/common';
import {GEAR_VIEW_LABELS} from '-/pages/gear/redesign/views';
import {SETTINGS_SECTIONS} from '-/pages/settings/sections';

import Skeleton from '@mui/material/Skeleton';

import {Breadcrumbs, DashboardGreeting as DashboardGreetingView} from '@embertracking/ui/components';

// Module-level cache of resolved breadcrumb docs keyed by full Firestore path
// (e.g. "gear/jrfd-scba/gear/jrfd-scba-cylinders"). Lives for the lifetime of
// the app — navigating between sibling items in the same category no longer
// re-fetches the parent category doc, and revisiting a previously viewed item
// renders instantly instead of flashing a skeleton.
//
// Trade-off: a category renamed in another tab won't update this tab's
// breadcrumbs until reload. Acceptable for an admin tool where renames are
// rare; the alternative is wiring an onSnapshot listener per breadcrumb,
// which would defeat the point of the cache (and leak listeners as the user
// moves through deep gear trees).
const breadcrumbCache = new Map();
const cacheKeyForArgs = args => args.slice(1).join('/');
const isSharedRequirementId = id => typeof id === 'string' && id.startsWith('hub_');

// `fallbackCollection` is read when the primary document isn't there. Members
// are the case that needs it: `usersLight` is the broadly-readable roster (#1000)
// while `users` carries the PII and is locked down, so a viewer who can open a
// member page can't necessarily read the doc this crumb was named from — and a
// roster entry can outlive its `users` doc. Falling back means the crumb says
// who the member is instead of a bare "User".
const generateDynamicBreadcrumbs = (collection, field, defaultValue, {fallbackCollection} = {}) => {
    const collections = castArray(collection);

    return ({match}) => {
        const {currentUser, users} = useContext(UserContext);

        const {params} = match;
        const keys = Object.keys(params);

        const args = useMemo(() => [
            db,
            ...keys.reduce((acc, key, index) => {
                acc.push(collections[index]);
                acc.push(params[key]);

                return acc;
            }, [])
        ], [db, keys]);

        const parentArgs = useMemo(() => {
            const result = args.slice(0, args.length - 2);
            if (result.length > 1) {
                return result;
            }

            return [];
        }, [args]);

        // Seed state from the module cache so re-navigation skips the skeleton
        // flash. If nothing's cached we fall back to null and the fetch below
        // populates both state and cache.
        const cacheKey = cacheKeyForArgs(args);
        const cached = breadcrumbCache.get(cacheKey);
        const [record, setRecord] = useState(cached?.record || null);
        const [parentRecord, setParentRecord] = useState(cached?.parentRecord || null);
        // "Still loading" and "there is no such document" are different states,
        // and conflating them rendered a Skeleton FOREVER for a missing or
        // unreadable doc — a grey bar where a name should be, with no error and
        // nothing to retry. Tracked separately so a finished-but-empty fetch
        // falls through to `defaultValue` instead.
        const [settled, setSettled] = useState(Boolean(cached));

        useEffect(() => {
            let isSubscribed = true;

            // We don't early-return on cache hit. The useState initializer
            // already seeded state from the cache (no skeleton flash), and
            // we still want the fetch to refresh the cache in the background
            // so a renamed category eventually catches up. Always running the
            // fetch also sidesteps a Strict-Mode race where the first effect
            // invocation's fetch promise hadn't resolved before the second
            // invocation, but a cache.set from elsewhere caused the second
            // invocation to early-return — leaving the seeded null state
            // stuck because no setRecord ever fired.
            const fetch = async() => {
                try {
                    const ref = doc(...args);
                    const parentRef = parentArgs.length > 0 ? doc(...parentArgs) : null;
                    const [raw, parentRaw] = await Promise.all([
                        getDoc(ref),
                        parentRef ? getDoc(parentRef) : Promise.resolve(null)
                    ]);

                    let parentDoc;
                    if (parentRaw && parentRaw.exists()) {
                        parentDoc = processRawDoc(parentRaw);
                    }

                    // Local variable name avoids shadowing the imported
                    // firestore `doc(...)` function we used above.
                    let resolved = processRawDoc(raw);

                    // Primary doc missing or unreadable — try the roster copy.
                    if (!resolved && fallbackCollection) {
                        const fallbackId = args[args.length - 1];
                        const fallbackRaw = await getDoc(doc(db, fallbackCollection, fallbackId)).catch(() => null);
                        resolved = processRawDoc(fallbackRaw);
                    }

                    // Two paths to resolving an apparatus reference into
                    // the apparatus object:
                    //   1. The field's id is literally 'apparatus' (older
                    //      categories) — `resolved.apparatus` is the uid.
                    //   2. The field's id is a uuid (current default in
                    //      CategoryDialog) — look it up via the parent
                    //      category's `additionalFields` and pull the uid
                    //      out of the matching key on the item.
                    // In both cases we replace the uid in place with the
                    // fetched apparatus object so downstream rendering
                    // sees `{tag, ...}`.
                    const apparatus = get(resolved, 'apparatus');
                    if (apparatus) {
                        resolved = await populateKeyFromCollection(db, resolved, 'apparatus', 'apparatus');
                    } else if (parentDoc) {
                        // A location title field (incl. legacy apparatus, #1405) holds
                        // a bare apparatus uid or a `gearLocation:<id>` value — resolve
                        // whichever kind into a doc so the render below reads .tag/.name.
                        const locationField = (parentDoc.additionalFields || []).find(f => f && isLocationField(f));
                        const parts = locationField && locationField.id ? parseLocationValue(get(resolved, locationField.id)) : null;
                        if (parts && parts.kind === LOCATION_KINDS.GEAR_LOCATION) {
                            const locSnap = await getDoc(doc(db, 'gearLocations', parts.uid));
                            if (locSnap.exists()) resolved = {...resolved, [locationField.id]: locSnap.data()};
                        } else if (parts) {
                            resolved = await populateKeyFromCollection(db, resolved, locationField.id, 'apparatus');
                        }
                    }

                    breadcrumbCache.set(cacheKey, {record: resolved, parentRecord: parentDoc});

                    if (isSubscribed) {
                        setRecord(resolved);
                        setParentRecord(parentDoc);
                    }
                } catch(e) {
                    console.warn('generateDynamicBreadcrumbs', e);
                } finally {
                    if (isSubscribed) setSettled(true);
                }
            };

            fetch();

            return () => isSubscribed = false;
        }, [cacheKey]);

        if (!record) {
            // Only a fetch still in flight gets a skeleton. Once it has settled
            // with nothing, show the fallback label rather than a bar forever.
            if (!settled) {
                return <Skeleton variant="text" width={60} />;
            }
            return defaultValue || args[args.length - 1];
        }

        if (field === true) {
            try {
                const {additionalFields = []} = parentRecord || {};
                const [firstField] = additionalFields;
                if (firstField) {
                    const {id, type} = firstField;
                    let rawValue = get(record, id);

                    if (type === 'user') {
                        const user = rawValue ? users.find(user => user.uid === rawValue) : null;
                        if (user) {
                            rawValue = get(user, 'fullName');
                        } else {
                            // No user assigned (or stale uid) — surface
                            // "Unassigned item" so the crumb doesn't fall
                            // through to a raw uid or the item id.
                            return 'Unassigned item';
                        }
                    } else if (isLocationField(firstField)) {
                        // The placement value was replaced with its doc by the
                        // populate step above — an apparatus (.tag) or a managed
                        // location (.name). Pull the display name out.
                        const label = get(rawValue, 'tag') || get(rawValue, 'name');
                        if (label) {
                            rawValue = label;
                        } else {
                            return 'Unassigned item';
                        }
                    }

                    return convertFieldValue(null, rawValue, record, currentUser) || defaultValue || get(record, 'id');
                }
            } catch(e) {
                console.warn('generateDynamicBreadcrumbs', e);
            }

            return defaultValue || get(record, 'id');
        }

        if (typeof field === 'function') {
            return field(record, {currentUser, users});
        }

        let rawValue = get(record, field);

        if (Array.isArray(field)) {
            const values = field.map(f => get(record, f)).filter(Boolean);
            if (values.length) {
                rawValue = values[0];
            }
        }

        if (rawValue) {
            return convertFieldValue(field, rawValue, record, currentUser) || defaultValue || get(record, 'id');
        }

        return get(record, field) || defaultValue || get(record, 'id');
    };
};

const RequirementBreadcrumb = ({match}) => {
    const {id} = match.params;
    const cacheKey = `requirements/${id}`;
    const cached = breadcrumbCache.get(cacheKey);
    const [name, setName] = useState(cached?.name || null);

    useEffect(() => {
        let isSubscribed = true;

        const fetch = async() => {
            try {
                const requirement = isSharedRequirementId(id)
                    ? (await getRequirementDefinition(id)).requirement
                    : processRawDoc(await getDoc(doc(db, 'requirements', id)));
                const resolvedName = requirement?.name || requirement?.id || 'Requirement';
                breadcrumbCache.set(cacheKey, {name: resolvedName});
                if (isSubscribed) setName(resolvedName);
            } catch(e) {
                console.warn('RequirementBreadcrumb', e);
                if (isSubscribed) setName('Requirement');
            }
        };

        fetch();

        return () => isSubscribed = false;
    }, [cacheKey, id]);

    return name || <Skeleton variant="text" width={60} />;
};

const convertFieldValue = (field, value, record, currentUser) => {
    if (!value) {
        return;
    }

    if (value.toDate) {
        const dateFormat = get(currentUser, 'settings.dateFormat') || 'DD/MM/YYYY';
        return moment(value.toDate()).format(dateFormat);
    } else if (field === 'apparatus') {
        const apparatus = get(record, 'apparatus');
        if (apparatus) {
            return get(apparatus, 'tag');
        }
    }

    return value;
};

const DynamicChecksBreadcrumbs = ({match}) => {
    const {params, pathname} = match;
    const {apparatusId, id} = params;
    const {currentUser} = useContext(UserContext);
    const dateFormatLong = get(currentUser, 'settings.dateFormatLong') || 'LLL';

    const cacheKey = `${pathname}#check`;
    const [doc, setDoc] = useState(() => breadcrumbCache.get(cacheKey) || null);

    useEffect(() => {
        let isSubscribed = true;

        const fetch = async() => {
            try {
                const refPath = pathname.split('/').slice(0, -2).filter(Boolean).join('/');
                const ref = collection(db, refPath, 'checks');
                const docs = await populateCollectionIds(db, ref, [id]);

                const found = docs[id];
                if (found) breadcrumbCache.set(cacheKey, found);

                if (isSubscribed) {
                    setDoc(found);
                }
            } catch(e) {
                console.warn('DynamicChecksBreadcrumbs', e);
            }
        };

        fetch();

        return () => isSubscribed = false;
    }, [cacheKey, id, apparatusId]);

    let {createdAt} = doc || {};
    if (createdAt) {
        createdAt = moment(createdAt.toDate()).format(dateFormatLong);
    }

    if (!doc) {
        return <Skeleton variant="text" width={60} />;
    }

    return createdAt || id;
};

const generateStartCase = (field, process) => {
    return ({match}) => {
        const {params} = match;
        const value = get(params, field);
        const string = startCase(value);
        return process ? process(string, value) : string;
    };
};

// `maxDepth` is the raw iteration count of the alternating category/item walk,
// not a category depth — see GEAR_ROUTE_ITERATIONS in gearDepth.js. It must stay
// in step with Router.js's generators or a routable page loses its breadcrumb.
export const generateGearBreadcrumbPaths = (maxDepth = GEAR_ROUTE_ITERATIONS) => {
    const paths = [
        {path: 'gear/:id', breadcrumb: generateDynamicBreadcrumbs(['gear'], 'name', 'Category')},
        {path: 'gear/:id/:type', breadcrumb: false} //generateStartCase('type', string => `${string} Check`)}
    ];

    let path = 'gear/:collectionId';
    let collectionNames = ['gear'];

    for (let depth = 1; depth <= maxDepth; depth++) {
        if (depth === 1) {
            // First level: gear/:collectionId/:id with breadcrumb as ['gear', 'items']
            paths.push({
                path: `${path}/items`,
                breadcrumb: false
            });

            paths.push({
                path: `${path}/items/:id`,
                breadcrumb: generateDynamicBreadcrumbs([...collectionNames, 'items'], true, 'Item')
            });

            paths.push({
                path: `${path}/items/:id/:type/:id`,
                breadcrumb: DynamicChecksBreadcrumbs
            });
        } else if (depth % 2 === 0) {
            // Even depth: add `/gear` for collection, then `/gear/:id` with breadcrumb ['gear', ..., 'items']
            path += '/gear';

            paths.push({
                path: `${path}/:id`,
                breadcrumb: generateDynamicBreadcrumbs([...collectionNames, 'gear', 'items'], 'name', 'Category')
            });

            // paths.push({
            //     path: `${path}/:id/:type`,
            //     breadcrumb: generateStartCase('type', string => `${string} Check`)
            // });

            paths.push({
                path: `${path}/:id/items`,
                breadcrumb: null
            });
        } else {
            // Odd depth: add `/:subCollectionIdX/:id` for item breadcrumb with ['gear', ..., 'items']
            //
            // These param names (subCollectionId0, 1, 2, …) deliberately don't match
            // Router.js's (subCollectionId1, 3, 5, …). It looks like a bug and isn't:
            // use-react-router-breadcrumbs matches against *this* table and zips its
            // own params positionally, so only the ordering matters, not the names.
            path += `/:subCollectionId${Math.floor((depth - 2) / 2)}`;
            paths.push({
                path: `${path}/items/:id`,
                breadcrumb: generateDynamicBreadcrumbs([...collectionNames, 'items'], true, 'Item')
            });

            paths.push({
                path: `${path}/items/:id/:type/:id`,
                breadcrumb: DynamicChecksBreadcrumbs
            });
        }

        // Update collectionNames to add 'gear' for each collection depth, ending with 'items' as needed
        if (depth % 2 !== 0) {
            collectionNames.push('gear');
        }
    }

    return paths;
};

const routes = [
    {path: '/stations/add', breadcrumb: 'Add station'},
    {path: '/stations/manage', breadcrumb: 'Edit stations'},
    {path: '/stations/:id/edit', breadcrumb: 'Edit'},
    // Hall checks (#1920) — same three entries the apparatus routes below get,
    // or a check reads as `weekly / <raw uid>`.
    {path: '/stations/:stationId/:type', breadcrumb: generateStartCase('type', string => `${string} Check`)},
    {path: '/stations/:stationId/:type/:id', breadcrumb: DynamicChecksBreadcrumbs},
    {path: '/stations/:stationId/:type/edit', breadcrumb: 'Edit'},
    {path: '/users/:id', breadcrumb: generateDynamicBreadcrumbs('users', ['fullName', 'email'], 'User', {fallbackCollection: 'usersLight'})},
    {path: '/users/:id/skills', breadcrumb: 'Skills'},
    {path: '/users/:id/transcript', breadcrumb: 'Transcript'},
    {path: '/users/:id/jibcConsent', breadcrumb: 'JIBC Consent Form'},
    // #1718. Without this the crumb falls back to the raw path segment and reads
    // "ServiceHistory".
    {path: '/users/:id/serviceHistory', breadcrumb: 'Service History'},
    {path: '/apparatus/:id', breadcrumb: generateDynamicBreadcrumbs('apparatus', 'tag', 'Apparatus')},
    {path: '/apparatus/add', breadcrumb: 'Add'},
    {path: '/apparatus/:apparatusId/edit', breadcrumb: 'Edit'},
    {path: '/apparatus/:apparatusId/:type', breadcrumb: generateStartCase('type', (string, value) => {
        if (value === 'pretrips') return 'Pre-Trip';
        if (value === 'bcwsReadiness') return 'BCWS Readiness Check';
        return `${string} Check`;
    })},
    {path: '/apparatus/:apparatusId/:type/:id', breadcrumb: DynamicChecksBreadcrumbs},
    {path: '/apparatus/:apparatusId/:type/edit', breadcrumb: 'Edit'},
    {path: '/apparatus/:apparatusId/:type/start', breadcrumb: 'Start'},
    {path: '/fuel', breadcrumb: 'Fuel'},
    {path: '/fuel/:id', breadcrumb: generateDynamicBreadcrumbs('fuelTanks', 'name', 'Tank')},
    {path: '/reports', breadcrumb: 'Reports'},
    // Without this the segment humanizes to "Help", which wouldn't match the
    // page's own heading.
    {path: '/help', breadcrumb: 'Help Center'},
    {path: '/reports/:reportId', breadcrumb: ({match}) => reportMeta(match.params.reportId).title || 'Report'},
    {path: '/matrix', breadcrumb: false},
    {path: '/matrix/qualification', breadcrumb: 'Qualification Matrix'},
    {path: '/matrix/training', breadcrumb: 'Training Matrix'},
    {path: '/calendar', breadcrumb: 'Calendar'},
    {path: '/dutyshifts', breadcrumb: 'Duty Shifts'},
    {path: '/dutyshifts/add', breadcrumb: 'Add'},
    {path: '/dutyshifts/:id', breadcrumb: generateDynamicBreadcrumbs('dutyShifts', (record, {currentUser, users}) => {
        const {startDate, endDate, members = []} = record;
        const dateFormat = get(currentUser, 'settings.dateFormat') || 'DD/MM/YYYY';

        if (!startDate || !endDate) return 'Duty Shift';

        const startStr = moment(startDate).format(dateFormat);
        const endStr = moment(endDate).format(dateFormat);
        const shiftType = dutyShiftTypeLabel(record);

        // Build date part
        let datePart;
        if (startStr === endStr) {
            datePart = startStr;
        } else {
            datePart = `${startStr} - ${endStr}`;
        }

        // Build members part
        let membersPart = '';
        if (members.length > 0) {
            if (members.length === 1) {
                const memberId = members[0];
                const user = users.find(u => u.uid === memberId || u.id === memberId);
                membersPart = ` - ${get(user, 'fullName', memberId)}`;
            } else if (members.length === 2) {
                const user1 = users.find(u => u.uid === members[0] || u.id === members[0]);
                const user2 = users.find(u => u.uid === members[1] || u.id === members[1]);
                const name1 = get(user1, 'fullName', members[0]);
                const name2 = get(user2, 'fullName', members[1]);
                membersPart = ` - ${name1} and ${name2}`;
            } else {
                const firstMember = members[0];
                const user = users.find(u => u.uid === firstMember || u.id === firstMember);
                const name = get(user, 'fullName', firstMember);
                const othersCount = members.length - 1;
                membersPart = ` - ${name} and ${othersCount} other${othersCount > 1 ? 's' : ''}`;
            }
        }

        return `${datePart}: ${shiftType}${membersPart}`;
    }, 'Duty Shift')},
    {path: '/profile', breadcrumb: 'Profile'},
    {path: '/profile/serviceHistory', breadcrumb: 'Service History'},
    {path: '/profile/skills', breadcrumb: 'Skills'},
    {path: '/profile/transcript', breadcrumb: 'Transcript'},
    {path: '/users', breadcrumb: 'Users'},
    {path: '/users/add', breadcrumb: 'Add'},
    {path: '/quizzes/fr', breadcrumb: 'First Responder Quiz'},
    {path: '/quizzes/fr/renewal', breadcrumb: 'Renewal Information'},
    {path: '/incidents', breadcrumb: 'Incidents'},
    {path: '/incidents/add', breadcrumb: 'Add'},
    {path: '/incidents/:id', breadcrumb: generateDynamicBreadcrumbs('incidents', ['incidentNumber', 'date'], 'Incident')},
    {path: '/incidents/import', breadcrumb: 'Import'},
    {path: '/training', breadcrumb: 'Training Records'},
    {path: '/regional-training', breadcrumb: 'Regional Calendar'},
    {path: '/regional-training/:id', breadcrumb: 'Regional Training'},
    {path: '/training/add', breadcrumb: 'Add'},
    {path: '/training/:id', breadcrumb: generateDynamicBreadcrumbs('training', ['title', 'raw.type', 'date'], 'Record')},
    {path: '/training/:id/jprs', breadcrumb: 'JPRs'},
    {path: '/training/keywords', breadcrumb: 'Keywords'},
    {path: '/training/import', breadcrumb: 'Import'},
    {path: '/lessonplans', breadcrumb: 'Lesson Plans'},
    {path: '/lessonplans/add', breadcrumb: 'Add'},
    {path: '/lessonplans/:id', breadcrumb: generateDynamicBreadcrumbs('lessonplans', 'name', 'Lesson Plan')},
    {path: '/lessonplans/:id/jprs', breadcrumb: 'JPRs'},
    {path: '/lessonplans/:id/:tab', breadcrumb: generateStartCase('tab')},
    {path: '/requirements', breadcrumb: 'Requirements'},
    {path: '/requirements/add', breadcrumb: 'Add'},
    // Skill-drill sections live under /requirements at clean URLs (/requirements/exterior,
    // /requirements/driverOperator, …). Their static keys outrank the :id route, so the
    // trail reads Requirements / Exterior Operations (not …/ Requirement / Exterior).
    // ALL sections, not just the progression ladder: Driver/Operator is standalone, and
    // listing only orderedSections() left it falling through to RequirementBreadcrumb,
    // which looks for a requirements/{id} document that a catalogue-backed section
    // doesn't have — so the crumb read the literal "Requirement" (#1579).
    ...allSkillDrillSections().map(s => ({path: `/requirements/${s.key}`, breadcrumb: s.label})),
    // Driving hours (#1580) is a catalogue-free sibling of those sections and needs naming
    // for the same reason: without this the crumb falls through to RequirementBreadcrumb,
    // which looks up a requirements/driverClearance document that doesn't exist.
    {path: '/requirements/driverClearance', breadcrumb: 'Driver Clearance'},
    {path: '/requirements/:id', breadcrumb: RequirementBreadcrumb},
    {path: '/requirements/:id/:tab', breadcrumb: generateStartCase('tab')},
    {path: '/assignments', breadcrumb: 'Assignments'},
    {path: '/assignments/:id', breadcrumb: generateDynamicBreadcrumbs('requirementAssignments', 'requirementName', 'Assignment')},
    {path: '/public-education', breadcrumb: 'Public Education'},
    {path: '/public-education/add', breadcrumb: 'Add'},
    {path: '/public-education/:id', breadcrumb: generateDynamicBreadcrumbs('publicEducation', 'title', 'Record')},
    {path: '/deployments', breadcrumb: 'Deployments'},
    {path: '/deployments/add', breadcrumb: 'Add'},
    {path: '/deployments/resources', breadcrumb: 'Reference'},
    {path: '/deployments/resources/how-to-fill-dtr', breadcrumb: 'How to fill out a DTR'},
    {path: '/deployments/:id', breadcrumb: generateDynamicBreadcrumbs('deployments', 'incidentNumber', 'Deployment')},
    {path: '/deployments/:id/:tab', breadcrumb: generateStartCase('tab')},
    {path: '/burn-permits', breadcrumb: 'Burn Permits'},
    {path: '/workzoom', breadcrumb: 'Workzoom'},
    {path: '/ohs', breadcrumb: 'OH&S Meetings'},
    {path: '/ohs/add', breadcrumb: 'Record'},
    {path: '/ohs/:id', breadcrumb: generateDynamicBreadcrumbs('training', 'title', 'Meeting')},
    {path: '/preplans', breadcrumb: 'Preplans'},
    {path: '/preplans/add', breadcrumb: 'Add'},
    {path: '/preplans/:id', breadcrumb: generateDynamicBreadcrumbs('preplans', record => {
        const {name, address} = record;
        const addressStr = address?.description || address?.formattedAddress || '';

        if (name && addressStr) {
            return `${name} - ${addressStr}`;
        }
        return name || addressStr || 'Preplan';
    }, 'Preplan')},
    {path: '/files', breadcrumb: 'Files'},
    {path: '/files/:categorySlug', breadcrumb: ({match}) => {
        const {fileCategories} = useContext(FileCategoriesContext);
        const slug = match.params.categorySlug;
        const category = fileCategories.find(c => c.slug === slug)
            || fileCategories.find(c => c.id === slug);
        if (!category) {
            return <Skeleton variant="text" width={60} />;
        }
        return category.name;
    }},
    {path: '/files/:categorySlug/folder', breadcrumb: false},
    {path: '/files/:categorySlug/folder/:folderId', breadcrumb: ({match}) => {
        const folderId = match.params.folderId;
        const cacheKey = `folder/${folderId}`;
        const [name, setName] = useState(() => breadcrumbCache.get(cacheKey) || null);

        useEffect(() => {
            let isSubscribed = true;

            const fetch = async () => {
                try {
                    const ref = doc(db, 'folders', folderId);
                    const snap = await getDoc(ref);
                    if (snap.exists()) {
                        const folderName = snap.data().name;
                        breadcrumbCache.set(cacheKey, folderName);
                        if (isSubscribed) setName(folderName);
                    }
                } catch(e) {
                    console.warn('Folder breadcrumb', e);
                }
            };
            fetch();
            return () => isSubscribed = false;
        }, [cacheKey, folderId]);

        if (!name) {
            return <Skeleton variant="text" width={60} />;
        }
        return name;
    }},
    {path: '/training-standards', breadcrumb: 'Training Standards'},
    {path: '/training-standards/:standard', breadcrumb: ({match}) => {
        const key = match.params.standard;
        if (key === 'wsbc-ohs') return 'WorkSafeBC';
        if (NFPA[key]) return `NFPA ${key}`;
        return key;
    }},
    {path: '/training-standards/:standard/:edition', breadcrumb: ({match}) => match.params.edition === 'current' ? null : `${match.params.edition} Edition`},
    // Section ids are single words ('dutyshifts', 'incidentforms'), so
    // startCase alone renders "Dutyshifts". Use the section's own label.
    {path: '/settings/:tab', breadcrumb: ({match}) => {
        const section = SETTINGS_SECTIONS.find(item => item.id === match.params.tab);
        return section ? section.label : startCase(match.params.tab);
    }},
    {path: '/gear', breadcrumb: 'Gear'},
    {path: '/gear/audits', breadcrumb: 'Member Gear Audits'},
    {path: '/gear/audits/:auditId', breadcrumb: 'Audit'},
    {path: '/gear/views', breadcrumb: false},
    {path: '/gear/views/:viewId', breadcrumb: ({match}) => GEAR_VIEW_LABELS[match.params.viewId] || startCase(match.params.viewId)},
    {path: '/gear/locations', breadcrumb: false},
    {path: '/gear/locations/apparatus', breadcrumb: false},
    {path: '/gear/locations/apparatus/:apparatusId', breadcrumb: generateDynamicBreadcrumbs('apparatus', 'tag', 'Apparatus')},
    {path: '/gear/locations/:locationId', breadcrumb: generateDynamicBreadcrumbs('gearLocations', 'name', 'Location')},
    {path: '/jibcSkills', breadcrumb: 'JIBC Skills'},
    {path: '/payroll', breadcrumb: 'Payroll'},
    {path: '/payroll/:periodId', breadcrumb: generateDynamicBreadcrumbs('payrollPeriods', record => {
        if (!record.startDate || !record.endDate) return 'Period';
        return `${moment(record.startDate).format('MMM D, YYYY')} - ${moment(record.endDate).format('MMM D, YYYY')}`;
    }, 'Period')},
    {path: '/admin-hours', breadcrumb: 'Admin Hours'},
    {path: '/admin-hours/:timesheetId', breadcrumb: generateDynamicBreadcrumbs('adminHoursTimesheets', record => {
        if (!record.startDate || !record.endDate) return 'Timesheet';
        return `${moment(record.startDate).format('MMM D, YYYY')} - ${moment(record.endDate).format('MMM D, YYYY')}`;
    }, 'Timesheet')},
    {path: '/ui', breadcrumb: 'UI'},
    ...generateGearBreadcrumbPaths()
];

const DashboardGreeting = () => {
    const {currentUser} = useContext(UserContext);
    const {siteName} = useContext(SettingsContext);
    const {firstName, fullName, email, isStation} = currentUser || {};
    let displayName;
    if (isStation) {
        // A station account's currentUser is the station doc spread flat, so
        // format it as a station rather than trusting the raw `name` (which may
        // already read "Station 1" — see @embertracking/common).
        displayName = stationDisplayName(currentUser);
    } else {
        displayName = firstName || (fullName && fullName.split(' ')[0]) || email || null;
    }

    useDocumentTitle('Dashboard');

    return <DashboardGreetingView displayName={displayName} siteName={siteName} />;
};

const HeaderBreadcrumbs = () => {
    let breadcrumbs = useBreadcrumbs(routes);
    const {pathname} = useLocation();
    const {stations = []} = useContext(StationsContext);
    // Station names already have a live listener. Use it for breadcrumbs too,
    // so renames update immediately and hall IDs never become display labels.
    if (pathname === '/stations' || pathname.startsWith('/stations/')) {
        const soleStationId = stations?.length === 1 ? (stations[0].uid || stations[0].id) : null;
        if (soleStationId && (pathname === `/stations/${soleStationId}` || pathname.startsWith(`/stations/${soleStationId}/`))) {
            breadcrumbs = breadcrumbs.filter(item => item.match.pathname !== '/stations');
        }
        breadcrumbs = breadcrumbs.map(item => {
            const path = item.match.pathname;
            let label;
            if (path === '/stations') label = stations?.length === 1 ? 'Station' : 'Stations';
            else if (/^\/stations\/[^/]+$/.test(path) && !['/stations/add', '/stations/manage'].includes(path)) {
                const id = path.split('/')[2];
                label = stationDisplayName(stations?.find(station => (station.uid || station.id) === id));
            }
            return label ? {...item, breadcrumb: <span>{label}</span>} : item;
        });
    }
    const [folderChain, setFolderChain] = useState([]);
    // Gear locations form a tree that isn't in the URL (only the leaf id), so the
    // breadcrumb bar needs the ancestor chain injected — mirrors folderChain below.
    const {byId: locationsById} = useGearLocations();

    // Fetch parent folder chain for nested folder breadcrumbs
    useEffect(() => {
        const folderMatch = pathname.match(/^\/files\/([^/]+)\/folder\/([^/]+)$/);
        if (!folderMatch) {
            setFolderChain([]);
            return;
        }

        const categorySlug = folderMatch[1];
        const folderId = folderMatch[2];

        let isSubscribed = true;
        const fetchChain = async () => {
            const chain = [];
            let currentId = folderId;

            while (currentId) {
                try {
                    const ref = doc(db, 'folders', currentId);
                    const snap = await getDoc(ref);
                    if (!snap.exists()) break;
                    const data = snap.data();
                    chain.unshift({id: currentId, name: data.name, parentFolderId: data.parentFolderId || null});
                    currentId = data.parentFolderId || null;
                } catch (e) {
                    break;
                }
            }

            if (isSubscribed) {
                setFolderChain(chain.map(f => ({...f, categorySlug})));
            }
        };

        fetchChain();
        return () => { isSubscribed = false; };
    }, [pathname]);

    let text;
    if (pathname !== '/' && breadcrumbs.length > 0) {
        const {breadcrumb} = breadcrumbs[breadcrumbs.length - 1];
        text = get(breadcrumb, 'props.children');
    }

    useDocumentTitle(text);

    if (pathname === '/') {
        return <DashboardGreeting />;
    }

    if (!breadcrumbs.length) {
        return null;
    }

    const items = breadcrumbs.reduce((result, item, index) => {
        const {breadcrumb, match, key} = item;
        const last = breadcrumbs.length - 1 === index;

        if (get(match, 'route.breadcrumb') === false) {
            return result;
        }

        if (/^\/training-standards\/[^/]+\/current$/.test(key)) {
            return result;
        }

        // Inject parent folder breadcrumbs before the current folder
        const isFolderBreadcrumb = /^\/files\/[^/]+\/folder\/[^/]+$/.test(key);
        if (isFolderBreadcrumb && folderChain.length > 1) {
            folderChain.slice(0, -1).forEach(folder => {
                const folderPath = `/files/${folder.categorySlug}/folder/${folder.id}`;
                result.push({label: folder.name, to: folderPath, key: folderPath, bold: false});
            });
        }

        // Inject ancestor locations before the current one, each clickable — the
        // leaf itself is added by the default push below.
        const locationMatch = key.match(/^\/gear\/locations\/([^/]+)$/);
        if (locationMatch) {
            locationPath(locationMatch[1], locationsById).slice(0, -1).forEach(anc => {
                const to = `/gear/locations/${anc.uid}`;
                result.push({label: anc.name, to, key: to, bold: false});
            });
        }

        // A recorded OH&S meeting still opens with the feature off, but the
        // list it sits under doesn't exist then (Router.js).
        const disableLink = key === '/matrix' || (key === '/ohs' && !hasFeature('ohs'))
            || get(match, 'route.breadcrumb.ignoreLink') === false;
        result.push({label: breadcrumb, to: key, key, disableLink, bold: last});

        return result;
    }, []);

    return <Breadcrumbs items={items} />;
};

export default HeaderBreadcrumbs;
